import styled, { css } from 'styled-components';
import { Button, Typography } from '@mui/material';
import { CheckCircleOutlineOutlined } from '@mui/icons-material';

import colors, { getContrastSafeColor } from '../../styles/colors';
import { mixins } from '../../styles/breakpoints';
import { checkoutPagesFontStyles } from '../../styles/text';

const Container = styled.div`
  ${checkoutPagesFontStyles};
  display: flex;
  align-items: center;
  flex-direction: column;
  width: 100%;
  height: 100%;
  min-height: 100vh;
  background-color: ${({ $isDarkTheme }) => ($isDarkTheme ? colors.shades.dark1 : colors.base.alpha)};
  background-size: cover;
  overflow: hidden;
  scroll-behavior: smooth;
  
`;

const HeaderContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 20px;
  margin: 20px 20px 24px 20px;

  ${mixins.tabletMedia(css`
    width: 750px;
  `)}

  ${mixins.desktopMedia(css`
    width: 900px;
  `)}

  ${mixins.upToTabletMedia(css`
    width: 100%;
    max-width: 450px;
    justify-content: flex-end;
  `)}
`;

const ContentContainer = styled.div`
  display: flex;
  flex-direction: row;
  align-items: flex-start;
  justify-content: center;
  min-height: fit-content;
  margin-top: 45px;
  margin-bottom: 20px;
  box-shadow: none;
  gap: 50px;
  width: 100%;

  ${mixins.tabletMedia(css`
    max-width: 720px;
  `)}
  ${mixins.desktopMedia(css`
    max-width: 900px;
  `)}
  ${mixins.upToDesktopMedia(css`
    gap: 40px;
  `)}
`;

const LeftSideWrapper = styled.div`
  display: flex;
  flex-direction: column;
  max-width: 450px;
  margin: 0 20px;
  ${mixins.upToTabletMedia(css`
    display: none;
  `)}
`;

const RightSideWrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  justify-content: center;
  max-width: 450px;
  background-color: ${({ $isDarkTheme }) => ($isDarkTheme ? colors.shades.dark1 : colors.base.alpha)};
  margin-top: 20px;

  ${mixins.upToTabletMedia(css`
    margin-top: 40px;
  `)}
`;

const StyledAppIcon = styled.img`
  width: 60px;
  height: 60px;
  object-fit: contain;

  ${mixins.upToTabletMedia(css`
    display: none;
  `)}
`;

const UserDetailsSection = styled.div`
  display: flex;
  align-items: center;
  gap: 20px;
  width: fit-content;

  ${mixins.upToTabletMedia(css`
    margin-right: 10px;
  `)}
`;

const UserName = styled(Typography)`
  color: ${colors.shades.gamma10};
  ${mixins.upToTabletMedia(css`
    display: none;
  `)}
`;

const LogoutButton = styled(Button)`
  background-color: ${({ $isDarkTheme }) => ($isDarkTheme ? colors.shades.dark2 : colors.shades.dark4)};
  color: ${({ $isDarkTheme }) => ($isDarkTheme ? colors.shades.dark4 : colors.base.beta)};
  width: 100px;
  height: 40px;
  border-radius: 20px;
  &:hover {
    background-color: ${({ $isDarkTheme }) => ($isDarkTheme ? colors.shades.dark2 : colors.shades.dark4)};
    opacity: 0.8;
  }
`;

const StyledAppPreview = styled.img`
  width: 450px;
  height: 450px;

  ${mixins.upToDesktopMedia(css`
    width: 350px;
    height: 350px;
  `)}
`;

const Title = styled.div`
  ${checkoutPagesFontStyles};
  font-size: 14px;
  letter-spacing: 0.04em;
  margin-bottom: 10px;
  font-weight: 300;
  color: ${({ $background, $isDarkTheme }) => getContrastSafeColor(
    $isDarkTheme ? colors.shades.dark1 : colors.base.alpha,
    $background || colors.shades.gamma10,
    $isDarkTheme ? colors.base.alpha : colors.base.beta,
  )};
`;

const DownloadText = styled.h1`
  ${checkoutPagesFontStyles};
  width: 100%;
  font-weight: 500;
  font-size: 32px;
  margin: 0 0 10px 0;
  padding-bottom: 10px;
  border-bottom: 1px solid ${({ $isDarkTheme }) => ($isDarkTheme ? colors.shades.dark2 : colors.shades.gamma3)};
  color: ${({ $isDarkTheme }) => ($isDarkTheme ? colors.base.alpha : colors.base.beta)};
`;

const ChecklistContainer = styled.div`
  margin: 20px 0;
  color: ${colors.shades.dark4};
  font-size: 14px;
  line-height: 1.6;
`;

const ChecklistTitle = styled.div`
  margin-bottom: 15px;
  color: ${colors.shades.gamma14};
  font-size: 12px;
`;

const ChecklistItem = styled.div`
  display: flex;
  align-items: center;
  gap: 5px;
  margin-bottom: 5px;
  span {
    font-weight: ${({ isBold }) => (isBold ? 'bold' : 'normal')};
  }
`;

const NextSteps = styled.div`
  span {
    color: ${({ $isDarkTheme }) => ($isDarkTheme ? colors.base.alpha : colors.base.beta)};
    font-weight: 500;
  }
`;

const StyledCheckIcon = styled(CheckCircleOutlineOutlined)`
  width: 15px;
  height: 15px;
`;

export {
  Container,
  HeaderContainer,
  ContentContainer,
  LeftSideWrapper,
  RightSideWrapper,
  StyledAppIcon,
  UserDetailsSection,
  LogoutButton,
  StyledAppPreview,
  Title,
  DownloadText,
  ChecklistContainer,
  ChecklistTitle,
  ChecklistItem,
  NextSteps,
  UserName,
  StyledCheckIcon,
};
